import { useEffect, useState } from "react";
import { Wallet, IndianRupee, ArrowDownCircle, ArrowUpCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const TXN_LABEL = {
  explorer_refund: "Refund",
  explorer_bonus: "Bonus",
  explorer_topup: "Top-up",
  explorer_booking_pay: "Booking payment",
  mentor_session_payout: "Session payout",
  mentor_cancel_payout: "Cancel payout",
  mentor_late_cancel_penalty: "Late-cancel penalty",
  mentor_course_cancel_charge: "Course cancel charge",
  settlement: "Bank settlement",
  admin_adjustment: "Admin adjustment",
};

function inr(n) {
  return `₹${(n || 0).toLocaleString("en-IN")}`;
}

export function WalletPanel({ role }) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankForm, setBankForm] = useState({ account_holder_name: "", account_number: "", ifsc: "", upi_id: "" });
  const [showBankForm, setShowBankForm] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [submittingSettle, setSubmittingSettle] = useState(false);
  const [settlementRequests, setSettlementRequests] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/wallet/me");
      setWallet(data);
      if (data?.bank_details) setBankForm({ ...data.bank_details, upi_id: data.bank_details.upi_id || "" });
      if (role === "teacher") {
        try {
          const sr = await api.get("/wallet/settlement-requests/me");
          setSettlementRequests(sr.data || []);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role]);

  const saveBank = async (e) => {
    e?.preventDefault?.();
    setSavingBank(true);
    try {
      await api.put("/wallet/bank-details", bankForm);
      toast.success("Bank details saved");
      setShowBankForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingBank(false);
    }
  };

  const submitSettle = async (e) => {
    e?.preventDefault?.();
    const amt = parseInt(settleAmount, 10);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSubmittingSettle(true);
    try {
      await api.post("/wallet/settlement-request", { amount: amt, note: settleNote });
      toast.success("Settlement request submitted");
      setShowSettleModal(false);
      setSettleAmount("");
      setSettleNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmittingSettle(false);
    }
  };

  const cancelSettleRequest = async (id) => {
    if (!window.confirm("Cancel this settlement request?")) return;
    try {
      await api.post(`/wallet/settlement-requests/${id}/cancel`);
      toast.success("Request cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (loading) return <p className="text-sm" style={{ color: "#13A699" }}>Loading wallet…</p>;

  const balance = wallet?.balance ?? 0;
  const txns = wallet?.transactions || [];

  return (
    <div data-testid={`wallet-panel-${role}`} className="space-y-4">
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-bold uppercase inline-flex items-center gap-2" style={{ color: "#13A699" }}>
              <Wallet className="h-5 w-5" /> Your Wallet
            </h2>
            <div className="mt-3 text-4xl sm:text-5xl font-bold font-display inline-flex items-center" style={{ color: "#13A699" }} data-testid="wallet-balance">
              <IndianRupee className="h-7 w-7" />{balance.toLocaleString("en-IN")}
            </div>
            <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
              {role === "teacher"
                ? "Net earnings (after 20% commission). Request a bank settlement anytime once you've added your bank details."
                : "Refunds, bonuses & admin credits land here. Use it to pay for bookings (when bookings move to paid)."}
            </p>
          </div>
          {role === "teacher" && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setShowBankForm((v) => !v)}
                className="text-xs font-bold px-3 py-2 rounded-full border border-[#13A699]"
                style={{ color: "#13A699" }}
                data-testid="wallet-bank-toggle"
              >
                {wallet?.bank_details ? "Edit Bank Details" : "+ Add Bank Details"}
              </button>
              <button
                type="button"
                onClick={() => setShowSettleModal(true)}
                disabled={balance <= 0 || !wallet?.bank_details}
                className="btn-yellow text-xs px-4 py-2 disabled:opacity-60"
                title={!wallet?.bank_details ? "Add bank details first" : balance <= 0 ? "Wallet is empty" : ""}
                data-testid="wallet-settle-btn"
              >
                Request Settlement
              </button>
            </div>
          )}
        </div>

        {role === "teacher" && showBankForm && (
          <form onSubmit={saveBank} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wallet-bank-form">
            <BankInput label="Account holder name" value={bankForm.account_holder_name} onChange={(v) => setBankForm({ ...bankForm, account_holder_name: v })} testId="bank-holder" />
            <BankInput label="Account number" value={bankForm.account_number} onChange={(v) => setBankForm({ ...bankForm, account_number: v })} testId="bank-number" />
            <BankInput label="IFSC" value={bankForm.ifsc} onChange={(v) => setBankForm({ ...bankForm, ifsc: v.toUpperCase() })} testId="bank-ifsc" />
            <BankInput label="UPI (optional)" value={bankForm.upi_id} onChange={(v) => setBankForm({ ...bankForm, upi_id: v })} testId="bank-upi" required={false} />
            <div className="md:col-span-2 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowBankForm(false)} className="text-xs font-bold px-3 py-2 rounded-full border border-[#13A699]" style={{ color: "#13A699" }}>Cancel</button>
              <button type="submit" disabled={savingBank} className="btn-yellow text-xs px-4 py-2 disabled:opacity-60" data-testid="bank-save">{savingBank ? "Saving..." : "Save Bank Details"}</button>
            </div>
          </form>
        )}

        {role === "teacher" && wallet?.bank_details && !showBankForm && (
          <div className="mt-3 text-xs rounded-xl px-3 py-2 inline-block" style={{ background: "#FFF7ED", color: "#13A699" }} data-testid="bank-saved">
            🏦 {wallet.bank_details.account_holder_name} · A/c ****{(wallet.bank_details.account_number || "").slice(-4)} · {wallet.bank_details.ifsc}
          </div>
        )}
      </div>

      {role === "teacher" && settlementRequests.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border border-black/5" data-testid="settlement-requests-card">
          <h3 className="font-display font-bold uppercase text-sm" style={{ color: "#13A699" }}>Settlement Requests</h3>
          <ul className="mt-2 space-y-2">
            {settlementRequests.map((r) => (
              <li key={r.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`settlement-row-${r.id}`}>
                <div>
                  <div className="text-sm font-bold" style={{ color: "#13A699" }}>{inr(r.amount)}</div>
                  <div className="text-[11px]" style={{ color: "#13A69999" }}>
                    {new Date(r.created_at).toLocaleString()}{r.note ? ` · ${r.note}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{
                    background: r.status === "paid" ? "#13A699" : r.status === "rejected" ? "#DC2626" : r.status === "cancelled" ? "#E5E7EB" : "#FFD708",
                    color: r.status === "paid" || r.status === "rejected" ? "#FFF7ED" : "#13A699",
                  }}>{r.status}</span>
                  {r.status === "pending" && (
                    <button onClick={() => cancelSettleRequest(r.id)} className="text-xs font-bold underline" style={{ color: "#DC2626" }} data-testid={`settlement-cancel-${r.id}`}>Cancel</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <h3 className="font-display font-bold uppercase text-sm" style={{ color: "#13A699" }}>Recent Transactions</h3>
        {txns.length === 0 ? (
          <p className="mt-2 text-xs" style={{ color: "#13A69999" }} data-testid="txn-empty">No transactions yet.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {txns.map((t) => {
              const positive = t.delta > 0;
              return (
                <li key={t.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0" data-testid={`txn-row-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {positive ? <ArrowDownCircle className="h-4 w-4 shrink-0" style={{ color: "#13A699" }} /> : <ArrowUpCircle className="h-4 w-4 shrink-0" style={{ color: "#DC2626" }} />}
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate" style={{ color: "#13A699" }}>
                        {TXN_LABEL[t.type] || t.type}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: "#13A69999" }}>
                        {t.note || ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold" style={{ color: positive ? "#13A699" : "#DC2626" }}>
                      {positive ? "+" : "-"}{inr(Math.abs(t.delta))}
                    </div>
                    <div className="text-[10px]" style={{ color: "#13A69999" }}>{new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Settlement request modal */}
      {showSettleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={(e) => { if (e.target === e.currentTarget) setShowSettleModal(false); }} data-testid="settle-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-black/5">
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>Request Bank Settlement</h3>
              <button onClick={() => setShowSettleModal(false)} className="text-[#13A699]" data-testid="settle-close"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submitSettle} className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Amount (₹)</label>
                <input
                  type="number"
                  min="1"
                  max={balance}
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)}
                  className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                  data-testid="settle-amount"
                  required
                />
                <p className="text-[11px] mt-1" style={{ color: "#13A69999" }}>Available: {inr(balance)}</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Note (optional)</label>
                <input
                  value={settleNote}
                  onChange={(e) => setSettleNote(e.target.value)}
                  className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                  data-testid="settle-note"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowSettleModal(false)} className="text-xs font-bold px-3 py-2 rounded-full border border-[#13A699]" style={{ color: "#13A699" }}>Cancel</button>
                <button type="submit" disabled={submittingSettle} className="btn-yellow text-xs px-4 py-2 disabled:opacity-60" data-testid="settle-submit">
                  {submittingSettle ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function BankInput({ label, value, onChange, testId, required = true }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
        data-testid={testId}
      />
    </div>
  );
}
